// U-4 `/upload/history` — read-only список загрузок + Sprint 5 G3 reopen action.
// Sources:
//   docs/specs/sprint-4/stage-4-frontend.md §4.4 (base)
//   docs/specs/sprint-5/stage-4-frontend.md §5.5 (G3 amend, Q-S5-S4-2 cut G1).

import { useMemo } from 'react';
import { Link, useLocation } from 'wouter';
import { ChevronRight, Loader2, Plus } from 'lucide-react';
import { useUploadHistory } from '@/api/hooks/uploads';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { BottomNav } from '@/components/layout/BottomNav';
import { setUploadOrigin, uploadsParent } from '@/lib/uploadNav';
import { Button } from '@/components/ui/button';
import { FileTypeBadge, UploadStatusChip } from '@/components/uploads/UploadStatusChip';
import { STATUS_LABEL, formatDate, formatSize, uploadTitle } from '@/lib/uploadDisplay';
import type { UploadListItem } from '@/api/types';

// Подписи статусов и форматтеры переехали в lib/uploadDisplay — их использует и
// экран анализа, а две копии на соседних экранах гарантированно разъедутся.
export { STATUS_LABEL };

const MONTH_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

function groupByMonth(items: UploadListItem[]): Array<{ key: string; label: string; rows: UploadListItem[] }> {
  const map = new Map<string, UploadListItem[]>();
  for (const it of items) {
    const d = new Date(it.created_at);
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const arr = map.get(key) ?? [];
    arr.push(it);
    map.set(key, arr);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([key, rows]) => {
      const [y, m] = key.split('-');
      return { key, label: `${MONTH_RU[Number(m) - 1]} ${y}`, rows };
    });
}

const FILES_FORMS = ['файл', 'файла', 'файлов'] as const;

function pluralFiles(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return FILES_FORMS[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return FILES_FORMS[1];
  return FILES_FORMS[2];
}

export function UploadHistory() {
  const [, setLocation] = useLocation();
  const { data, isLoading, isError } = useUploadHistory();
  const groups = useMemo(() => groupByMonth(data?.data ?? []), [data]);
  const total = data?.meta?.total ?? 0;

  // HEAL-82: возврат в РАЗДЕЛ, откуда человек пришёл.
  //
  // Прежний `window.history.back()` вёл туда, где он физически был, — а это
  // могла быть удалённая страница, перенаправление или место вне приложения.
  //
  // Но и «всегда Главная» неверно: сюда попадают из Профиля, со строки на
  // Главной и после подтверждения анализа. Человек, пришедший из Профиля, ждёт
  // возврата в Профиль — это базовое ожидание, а не тонкость.
  // Замена записи, а не добавление (круг ревью, находка 4): иначе аппаратная
  // кнопка «назад» на телефоне отменяет переход и возвращает человека на экран,
  // который он только что покинул, а стопка истории растёт при каждом тычке.
  const goBack = () => setLocation(uploadsParent(), { replace: true });

  return (
    <MobileFrame>
      <ScreenHeader onBack={goBack} title="Мои загрузки" />
      {isLoading && (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      {isError && !isLoading && (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
          <p className="text-sm text-muted-foreground">Не удалось загрузить историю.</p>
        </div>
      )}
      {!isLoading && !isError && total === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-sm text-muted-foreground">Пока ничего нет.</p>
          <Button asChild>
            <Link href="/upload">Загрузить первый анализ →</Link>
          </Button>
        </div>
      )}
      {!isLoading && !isError && total > 0 && (
        <div className="flex flex-1 flex-col overflow-y-auto px-5 py-4">
          <div className="mb-4 text-hl-small text-muted-foreground">
            {total} {pluralFiles(total)}
          </div>
          {groups.map((group) => (
            <section key={group.key} className="mb-5 last:mb-0">
              <h2 className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
              </h2>
              <div className="space-y-2">
                {group.rows.map((row) => (
                  <UploadRow key={row.id} row={row} />
                ))}
              </div>
            </section>
          ))}
          <Button asChild className="mt-1 w-full" size="lg">
            <Link href="/upload">
              <Plus className="mr-2 h-4 w-4" />
              Загрузить новый
            </Link>
          </Button>
        </div>
      )}
      {/* HEAL-82: до этого экран был тупиком — выйти можно было только стрелкой,
          хотя попадают сюда как в раздел: из Профиля и со строки о непроверенных.
          Без подсветки вкладки: своей у «Моих загрузок» нет, а подсветить чужую
          значило бы соврать. */}
      <BottomNav />
    </MobileFrame>
  );
}

export function UploadRow({ row }: { row: UploadListItem }) {
  const status = STATUS_LABEL[row.status];
  const dateLabel = row.measured_at ?? row.created_at.slice(0, 10);

  // HEAL-44a: строка стала живой. До этого она была тупиком — провалиться некуда,
  // файл не открыть, показатели не посмотреть, ничего не удалить.
  return (
    <Link
      href={`/uploads/${row.id}`}
      // HEAL-82: объявляем раздел, из которого уходим, — иначе «Назад» из
      // карточки файла, входящего в пакет, уводило на экран пакета, и человек
      // выпадал из «Моих загрузок» (найдено на устройстве 24.09).
      onClick={() => setUploadOrigin('history')}
      className="flex items-center gap-3 rounded-surface border border-border bg-surface p-3 transition-colors active:bg-muted"
    >
      <FileTypeBadge mimeType={row.mime_type} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-hl-secondary font-semibold">{uploadTitle(row)}</div>
        <div className="text-hl-small text-muted-foreground">
          {formatDate(dateLabel)} · {formatSize(row.file_size)}
        </div>
      </div>
      <UploadStatusChip tone={status.tone} text={status.text} failure={row.failure_reason} />
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300" />
    </Link>
  );
}
